import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Lock, ExternalLink, X, AlertCircle, Brain, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getClients, createClient, type CoachingClient } from '../../lib/coaching-sessions';

interface OutletCtx { isUnlocked: boolean; }

const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

const DEMO_CLIENTS = [
  {
    name: 'Alex',
    goal: 'Grow coaching business and improve visibility',
    theme: 'Visibility and confidence',
    breakthrough: 'Fear of judgement',
    nextFocus: 'Identity around being seen',
    sessions: 3,
    initials: 'AL',
    color: 'bg-blue-500',
  },
  {
    name: 'Sarah',
    goal: 'Improve boundary-setting and reduce resentment',
    theme: 'Boundaries and resentment',
    breakthrough: 'Saying no without guilt',
    nextFocus: 'Values conflict: peace vs approval',
    sessions: 3,
    initials: 'SA',
    color: 'bg-success-40',
  },
  {
    name: 'David',
    goal: 'Follow through on commitments and reduce avoidance',
    theme: 'Follow-through and avoidance',
    breakthrough: 'Internal conflict around success',
    nextFocus: 'Ambition vs safety',
    sessions: 3,
    initials: 'DA',
    color: 'bg-orange-40',
  },
];

function DemoLabel() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold">
      Demo Preview
    </span>
  );
}

export function SIClientsPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();
  const { user } = useAuth();
  const [coachId, setCoachId] = useState<string | null>(null);
  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [formError, setFormError] = useState('');
  const [clientForm, setClientForm] = useState({ full_name: '', email: '', goals: '' });
  const [expandedDemo, setExpandedDemo] = useState<string | null>(null);

  useEffect(() => {
    if (!isUnlocked || !user) return;
    const load = async () => {
      setLoadingClients(true);
      const { data: coach } = await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle();
      if (!coach) return;
      setCoachId(coach.id);
      const c = await getClients(coach.id);
      setClients(c);
      setLoadingClients(false);
    };
    load();
  }, [isUnlocked, user]);

  const handleAddClient = async () => {
    if (!coachId || !clientForm.full_name.trim()) { setFormError('Name is required'); return; }
    setFormError('');
    setAddingClient(true);
    try {
      const { data, error } = await createClient({
        coach_id: coachId,
        full_name: clientForm.full_name.trim(),
        email: clientForm.email.trim(),
      });
      if (error) throw error;
      if (data) {
        setClients(prev => [data as CoachingClient, ...prev]);
        setClientForm({ full_name: '', email: '', goals: '' });
        setShowAddClient(false);
      }
    } catch {
      setFormError('Failed to add client. Please try again.');
    } finally {
      setAddingClient(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-80">Clients</h1>
            <p className="text-sm text-gray-50 mt-1">
              {isUnlocked
                ? 'Manage your client records, track session history, and map patterns over time.'
                : 'Create client records, track session history, and build a clearer map of each client\'s patterns over time.'}
            </p>
          </div>
          {isUnlocked && (
            <button
              onClick={() => { setShowAddClient(true); setFormError(''); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-success-40 hover:bg-success-50 text-white rounded-2xl text-sm font-semibold transition-colors flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              Add Client
            </button>
          )}
        </div>
      </motion.div>

      {/* Pre-unlock workspace card */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-6"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gray-10 rounded-xl flex-shrink-0">
              <Lock className="w-5 h-5 text-gray-40" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-80 mb-1">Your Client Workspace</h2>
              <p className="text-sm text-gray-50 leading-relaxed mb-4">
                Your own client records will unlock after the walkthrough. Below are sample clients to show you what the workspace looks like.
              </p>
              <a
                href={WALKTHROUGH_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                Unlock Client Workspace on the Walkthrough
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </motion.div>
      )}

      {/* Demo clients */}
      {!isUnlocked && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-sm font-semibold text-gray-60">Sample Clients</h2>
            <DemoLabel />
          </div>
          {DEMO_CLIENTS.map((client, i) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
            >
              <button
                className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-brand-5 transition-colors"
                onClick={() => setExpandedDemo(expandedDemo === client.name ? null : client.name)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full ${client.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                    {client.initials}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-80">{client.name}</p>
                    <p className="text-xs text-gray-40">{client.sessions} sessions · {client.theme}</p>
                  </div>
                </div>
                {expandedDemo === client.name
                  ? <ChevronUp className="w-4 h-4 text-gray-40" />
                  : <ChevronDown className="w-4 h-4 text-gray-40" />
                }
              </button>
              <AnimatePresence>
                {expandedDemo === client.name && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 border-t border-gray-10 pt-4 grid sm:grid-cols-2 gap-3">
                      {[
                        { label: 'Client Goal', value: client.goal },
                        { label: 'Key Theme', value: client.theme },
                        { label: 'Breakthrough Zone', value: client.breakthrough },
                        { label: 'Next Session Focus', value: client.nextFocus },
                      ].map(field => (
                        <div key={field.label}>
                          <p className="text-xs font-semibold text-gray-40 uppercase tracking-wide mb-0.5">{field.label}</p>
                          <p className="text-sm text-gray-60">{field.value}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      {/* Real clients (unlocked) */}
      {isUnlocked && (
        <div>
          {loadingClients ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : clients.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-20 p-12 text-center">
              <div className="p-4 bg-success-10 rounded-full w-fit mx-auto mb-4">
                <Users className="w-8 h-8 text-success-50" />
              </div>
              <p className="text-base font-semibold text-gray-80 mb-1">No clients yet</p>
              <p className="text-sm text-gray-50 mb-5 max-w-sm mx-auto">
                Add your first client to start tracking sessions and generating insights.
              </p>
              <button
                onClick={() => { setShowAddClient(true); setFormError(''); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-success-40 hover:bg-success-50 text-white rounded-2xl text-sm font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add First Client
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map(client => (
                <div key={client.id} className="bg-white rounded-2xl border border-gray-20 px-5 py-4 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {client.full_name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-80 truncate">{client.full_name}</p>
                    {client.email && <p className="text-xs text-gray-40 truncate">{client.email}</p>}
                  </div>
                  <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-10 text-success-50 hover:bg-success-40 hover:text-white rounded-xl text-xs font-semibold transition-colors">
                    <Brain className="w-3.5 h-3.5" />
                    Analyse Session
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Client Modal */}
      <AnimatePresence>
        {showAddClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={e => { if (e.target === e.currentTarget) setShowAddClient(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-soft-lg p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-gray-80">Add Client</h2>
                <button onClick={() => setShowAddClient(false)} className="p-1.5 hover:bg-gray-10 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-gray-50" />
                </button>
              </div>
              {formError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-xl mb-4 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {formError}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-80 mb-1.5">Full Name *</label>
                  <input
                    type="text" value={clientForm.full_name}
                    onChange={e => setClientForm(p => ({ ...p, full_name: e.target.value }))}
                    placeholder="e.g. Jane Smith" autoFocus
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-80 mb-1.5">Email (optional)</label>
                  <input
                    type="email" value={clientForm.email}
                    onChange={e => setClientForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="e.g. jane@example.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddClient(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-60 bg-gray-10 hover:bg-gray-20 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddClient}
                  disabled={addingClient || !clientForm.full_name.trim()}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-success-40 hover:bg-success-50 rounded-xl transition-colors disabled:opacity-60"
                >
                  {addingClient ? 'Adding...' : 'Add Client'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
